import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * A single template variable available for a notification event, as returned
 * by GET /admin/conversations/notification-event-variables?eventKey={key}
 * (apps/api Task 2 route).
 */
export interface VariableDef {
  name: string
  label: string
  example: string
}

export interface UseEventVariablesReturn {
  variables: VariableDef[]
  loading: boolean
  error: string | null
}

// Module-level stable fallback so consumers don't see a fresh [] each render.
const EMPTY_VARIABLES: VariableDef[] = []
const STALE_MS = 5 * 60 * 1000
const cache = new Map<string, { items: VariableDef[]; fetchedAt: number }>()

/**
 * Fetches the template variable catalog for a notification event from
 * GET /admin/conversations/notification-event-variables?eventKey={key}.
 * Caches per eventKey at module level so the template editor's variable
 * palette doesn't refetch. An empty eventKey resolves to no variables
 * without a network call.
 */
export function useEventVariables(eventKey: string): UseEventVariablesReturn {
  const cached = cache.get(eventKey)
  const [variables, setVariables] = useState<VariableDef[]>(
    cached?.items ?? EMPTY_VARIABLES,
  )
  const [loading, setLoading] = useState(eventKey !== '' && cached === undefined)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<{ variables: VariableDef[] }>(
        `/admin/conversations/notification-event-variables?eventKey=${encodeURIComponent(eventKey)}`,
      )
      const items = resp.variables ?? EMPTY_VARIABLES
      cache.set(eventKey, { items, fetchedAt: Date.now() })
      setVariables(items)
    } catch (e) {
      setError((e as Error).message)
      setVariables(EMPTY_VARIABLES)
    } finally {
      setLoading(false)
    }
  }, [eventKey])

  useEffect(() => {
    if (eventKey === '') {
      setVariables(EMPTY_VARIABLES)
      setLoading(false)
      setError(null)
      return
    }
    const c = cache.get(eventKey)
    if (c !== undefined && Date.now() - c.fetchedAt <= STALE_MS) {
      setVariables(c.items)
      setLoading(false)
      return
    }
    void load()
  }, [eventKey, load])

  return { variables, loading, error }
}

/** Test-only: clears the module-level cache. */
export function __resetEventVariablesCache(): void {
  cache.clear()
}
